import {
  Box,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import React from "react";
import { toast } from "react-toastify";
import apiHelper from "../../../util/ApiHelper";
import {
  formatDatetime,
} from "../../../util";
import RedeemIcon from '@material-ui/icons/Redeem';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import LocalOfferIcon from '@material-ui/icons/LocalOffer';

const useStyles = makeStyles((theme) => ({
  innerBox: {
    background: "#ffffff",
    minWidth: "1000px",
    marginBottom: theme.spacing(4),
    "& *": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionBox: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "300",
    fontSize: "13px",
    lineHeight: "15px",
    color: "#333333",
  },
  itemTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#333333",
  },
  itemValue: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#333333",
  },
  productTypeIcon: {
    marginRight: "8px",
    verticalAlign: "middle",
  },
  voucherCode: {
    fontFamily: "monospace",
    fontSize: "12px",
    backgroundColor: "#f5f5f5",
    padding: "4px 8px",
    borderRadius: "4px",
    border: "1px solid #ddd",
  },
}));

function formatCodeChips(codes, chipClassName) {
  if (!codes?.length) {
    return null;
  }
  if (!Array.isArray(codes)) {
    return <span className={chipClassName}>{codes}</span>;
  }
  return codes.map((code, index) => (
    <React.Fragment key={`${code}-${index}`}>
      {index > 0 ? " " : null}
      <span className={chipClassName}>{code}</span>
    </React.Fragment>
  ));
}

const OrderDetailsPage = (props) => {
  const classes = useStyles();
  const { match, disabled } = props;
  const { id } = match.params;
  const [orderData, setOrderData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const getOrderDetails = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.cineclub.getShopOrderById(id);
      console.log('Order Details:', result);
      setOrderData(result.data);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    getOrderDetails();
  }, [getOrderDetails]);

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Cineclub > Order Details
        </Typography>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!orderData) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Cineclub > Order Details
        </Typography>
        <Typography>Order not found</Typography>
      </Box>
    );
  }

  const renderProductType = () => {
    if (orderData.productType === "VOUCHER") {
      return (
        <>
          <RedeemIcon 
            style={{ color: "#20C931", fontSize: "medium" }} 
            className={classes.productTypeIcon}
          />
          Voucher
        </>
      );
    } else if (orderData.productType === "MERCHANDISE") {
      return (
        <>
          <LocalShippingIcon 
            style={{ color: "#306F9D", fontSize: "medium" }} 
            className={classes.productTypeIcon}
          />
          Merchandise
        </>
      );
    } else if (orderData.productType === "PROMO_CODE") {
      return (
        <>
          <LocalOfferIcon 
            style={{ color: "#FF9800", fontSize: "medium" }} 
            className={classes.productTypeIcon}
          />
          Coupon
        </>
      );
    }
    return orderData.productType || "";
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Cineclub > Order Details
      </Typography>
      
      <fieldset disabled={disabled} className="fieldset">
        <Box className={classes.innerBox}>
          <Box className={classes.sectionBox}>
            <Grid container xs={12} direction="row">
              <Grid item xs={6}>
                <strong>Order #{orderData.id}</strong>
              </Grid>
              <Grid item xs={6}>
                <Box
                  width="100%"
                  component="span"
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <strong>Redeem Time &nbsp; </strong>{" "}
                  {formatDatetime(orderData.updateTime)}
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          
          {/* ORDER DETAILS */}
          <Box className={classes.sectionBox}>
            <Grid container xs={12} direction="row" spacing={3}>
              <Grid container item xs={12} direction="row">
                <Typography className={classes.sectionTitle}>
                  Order Details
                </Typography>
              </Grid>
              <Grid container item xs={12} direction="row" spacing={3}>
                <Grid container item direction="column" spacing={1} xs={3}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Order Reference
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      {orderData.ref || "--"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction="column" spacing={1} xs={4}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Product Type
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      {renderProductType()}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction="column" spacing={1} xs={5}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Points Used
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      {orderData.pointUsed} pts
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container item xs={12} direction="row" spacing={3}>
                <Grid container item direction="column" spacing={1} xs={3}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Quantity
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      {orderData.quantity}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction="column" spacing={1} xs={4}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Price
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      HK${orderData.price}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction="column" spacing={1} xs={5}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Product Title
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      {orderData.title}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          
          {/* CUSTOMER DETAILS */}
          <Box className={classes.sectionBox}>
            <Grid container xs={12} direction="row" spacing={3}>
              <Grid container item xs={12} direction="row">
                <Typography className={classes.sectionTitle}>
                  Customer Details
                </Typography>
              </Grid>
              <Grid container item xs={12} direction="row" spacing={3}>
                <Grid container item direction="column" spacing={1} xs={6}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Name
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      {orderData.name}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction="column" spacing={1} xs={6}>
                  <Grid item>
                    <Typography className={classes.itemTitle}>
                      Email
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography className={classes.itemValue}>
                      {orderData.email}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          
          {/* PRODUCT-SPECIFIC DETAILS */}
          <Box className={classes.sectionBox}>
            <Grid container xs={12} direction="row" spacing={3}>
              <Grid container item xs={12} direction="row">
                <Typography className={classes.sectionTitle}>
                  {orderData.productType === "VOUCHER" 
                    ? "Voucher Details" 
                    : orderData.productType === "PROMO_CODE" 
                    ? "Coupon Details" 
                    : "Merchandise Details"}
                </Typography>
              </Grid>
              <Grid container item xs={12} direction="row" spacing={3}>
                {orderData.productType === "VOUCHER" && (
                  <Grid container item direction="column" spacing={1} xs={6}>
                    <Grid item>
                      <Typography className={classes.itemTitle}>
                        Voucher Code
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography className={classes.itemValue}>
                        {orderData.voucherCode?.length ? (
                          formatCodeChips(
                            orderData.voucherCode,
                            classes.voucherCode
                          )
                        ) : (
                          "--"
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
                
                {orderData.productType === "PROMO_CODE" && (
                  <Grid container item direction="column" spacing={1} xs={6}>
                    <Grid item>
                      <Typography className={classes.itemTitle}>
                        Coupon Code
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography className={classes.itemValue}>
                        {orderData.promoCode?.length ? (
                          formatCodeChips(
                            orderData.promoCode,
                            classes.voucherCode
                          )
                        ) : (
                          "--"
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                )}

                {orderData.productType === "MERCHANDISE" && (
                  <>
                    <Grid container item direction="column" spacing={1} xs={6}>
                      <Grid item>
                        <Typography className={classes.itemTitle}>
                          Option ID
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography className={classes.itemValue}>
                          {orderData.optionId || "--"}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container item direction="column" spacing={1} xs={6}>
                      <Grid item>
                        <Typography className={classes.itemTitle}>
                          Option Name
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography className={classes.itemValue}>
                          {orderData.optionName || "--"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </fieldset>
    </Box>
  );
};

export default OrderDetailsPage;
